import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VideoIcon } from './icons/VideoIcon';
import { getMediaStreamWithFallback } from '../lib/media-utils';
import { captureException } from '../lib/telemetry/posthog/frontend';

interface VideoPreviewProps {
  onDeviceSelect: (deviceId: string) => void;
  selectedDeviceId?: string;
  disabled?: boolean;
  showRecordingIndicator?: boolean;
  className?: string;
  hideDeviceSelector?: boolean;
  initialStream?: MediaStream | null;
  /** When true, hides the "Continue without camera" escape hatch on dismiss */
  requireVideo?: boolean;
  /** Fires when camera availability changes (e.g. denied → false, retry success → true) */
  onCameraAvailable?: (available: boolean) => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  onDeviceSelect,
  selectedDeviceId,
  disabled = false,
  showRecordingIndicator = false,
  className = '',
  hideDeviceSelector = false,
  initialStream = null,
  requireVideo = false,
  onCameraAvailable,
}) => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedId, setSelectedId] = useState<string>(selectedDeviceId || '');
  const [permissionError, setPermissionError] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionDismissed, setPermissionDismissed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onDeviceSelectRef = useRef(onDeviceSelect);

  // Keep ref updated
  useEffect(() => {
    onDeviceSelectRef.current = onDeviceSelect;
  }, [onDeviceSelect]);

  // Sync external selectedDeviceId prop to internal state
  useEffect(() => {
    if (selectedDeviceId && selectedDeviceId !== selectedId && hasInitialized) {
      setSelectedId(selectedDeviceId);
    }
  }, [selectedDeviceId]);

  // Initial permission request and device enumeration - runs ONCE
  useEffect(() => {
    if (hasInitialized) return undefined;

    /** Set to true on cleanup so a getUserMedia result that resolves after
     * unmount stops its tracks immediately instead of keeping the camera on. */
    let cancelled = false;

    const init = async () => {
      setIsLoading(true);
      try {
        let stream: MediaStream;

        if (initialStream && initialStream.getVideoTracks().length > 0) {
          // Permission already granted by parent - use the provided stream
          stream = initialStream;
        } else {
          // Request permission ourselves
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: 'user'
            }
          });
        }

        // Component unmounted while getUserMedia was in flight: stop all tracks
        // immediately so the camera light does not linger.
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = allDevices.filter(d => d.kind === 'videoinput');

        setDevices(videoInputs);

        if (videoInputs.length > 0) {
          const defaultDevice = videoInputs.find(d => d.deviceId === 'default') || videoInputs[0];
          const deviceToSelect = selectedDeviceId || defaultDevice.deviceId;
          setSelectedId(deviceToSelect);
          onDeviceSelectRef.current(deviceToSelect);

          // Keep the stream for preview instead of stopping and restarting
          // Try to switch to selected device if different from what we got
          const currentDeviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
          if (currentDeviceId === deviceToSelect) {
            // Already on correct device, keep stream
            streamRef.current = stream;
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          } else {
            // Need different device, stop this one
            stream.getTracks().forEach(t => t.stop());
          }
        } else {
          stream.getTracks().forEach(t => t.stop());
        }
        onCameraAvailable?.(true);
        setHasInitialized(true);
      } catch (e: unknown) {
        // getUserMedia rejects with a DOMException carrying `.name`/`.message`.
        const err = e as { name?: string; message?: string } | null | undefined;
        const errName = err?.name;
        const errMessage = err?.message;
        // Chrome fires "Permission dismissed" when the user closes the prompt
        // without choosing Allow/Block - this is retryable.
        const isDismissed =
          (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') &&
          errMessage === 'Permission dismissed';

        if (isDismissed) {
          setPermissionDismissed(true);
          onCameraAvailable?.(false);
          return; // finally still runs → setIsLoading(false)
        }

        console.error("Camera permission denied or error:", e);
        captureException(e instanceof Error ? e : String(e), 'VideoPreview.requestCameraPermission', { errorName: errName });
        if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') {
          setPermissionDenied(true);
        } else {
          setPermissionError(true);
        }
        onCameraAvailable?.(false);
        setHasInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };
    void init();

    return () => {
      cancelled = true;
    };
  }, [hasInitialized, selectedDeviceId, initialStream, retryCount]);

  // Handle stream for video preview when device changes
  useEffect(() => {
    if (!hasInitialized || !selectedId || disabled || permissionError || permissionDenied) return undefined;

    // Check if we already have a stream for this device
    const currentDeviceId = streamRef.current?.getVideoTracks()[0]?.getSettings()?.deviceId;
    if (currentDeviceId === selectedId && streamRef.current?.active) {
      // Already have correct stream, just ensure video element has it
      if (videoRef.current && videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
      }
      return undefined;
    }

    /** Set to true on cleanup so a getMediaStreamWithFallback result that
     * resolves after unmount stops its tracks instead of keeping the camera on. */
    let cancelled = false;

    const startStream = async () => {
      // Cleanup previous stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      try {
        const result = await getMediaStreamWithFallback({
          deviceId: selectedId,
          kind: 'video',
          constraints: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user',
          },
          context: 'video_preview_switch',
        });

        // Component unmounted while the promise was in flight: stop all tracks
        // immediately so the camera light does not linger.
        if (cancelled) {
          result.stream.getTracks().forEach(t => t.stop());
          return;
        }

        const stream = result.stream;
        streamRef.current = stream;

        // If fallback was used, sync selectedId to actual device
        if (result.fallbackUsed && result.actualDeviceId) {
          setSelectedId(result.actualDeviceId);
          onDeviceSelectRef.current(result.actualDeviceId);
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (e) {
        console.error("Error accessing camera for preview:", e);
        captureException(e instanceof Error ? e : String(e), 'VideoPreview.getCameraStream', { selectedId });
        setPermissionError(true);
      }
    };
    void startStream();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [selectedId, disabled, permissionError, permissionDenied, hasInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleDeviceChange = useCallback((deviceId: string) => {
    setSelectedId(deviceId);
    onDeviceSelectRef.current(deviceId);
  }, []);

  if (permissionDismissed) {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative bg-background rounded-xl overflow-hidden border border-border">
          <div className="aspect-video flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-lg text-muted-foreground">
              Camera access is needed to {requireVideo ? 'continue' : 'record video'}.
            </p>
            <button
              onClick={() => {
                setPermissionDismissed(false);
                setRetryCount(c => c + 1);
              }}
              className="px-4 py-2 rounded-lg bg-volt/15 hover:bg-volt/15 text-foreground text-sm transition-colors"
            >
              Allow camera access
            </button>
            {!requireVideo && (
              <button
                onClick={() => {
                  setPermissionDismissed(false);
                  setPermissionDenied(true);
                  setHasInitialized(true);
                }}
                className="text-sm text-muted-foreground hover:text-muted-foreground underline"
              >
                Continue without camera
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative bg-background rounded-xl overflow-hidden border border-border">
          <div className="aspect-video flex flex-col items-center justify-center text-center">
            <VideoIcon className="w-8 h-8 mx-auto mb-2 text-danger opacity-50" />
            <p className="text-lg font-medium text-danger">Camera access denied</p>
            <p className="text-sm mt-1 text-muted-foreground">
              Please enable camera permissions in your browser settings to use video recording.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permissionError) {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative bg-background rounded-xl overflow-hidden border border-border">
          <div className="aspect-video flex flex-col items-center justify-center text-center">
            <VideoIcon className="w-8 h-8 mx-auto mb-2 text-warning opacity-50" />
            <p className="text-lg font-medium text-warning">Camera not available</p>
            <p className="text-sm mt-1 text-muted-foreground">
              No camera detected or there was an error accessing your camera.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Video Preview Box */}
      <div className="relative bg-background rounded-xl overflow-hidden border border-border">
        <div className="aspect-video relative">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-volt" />
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          )}

          {/* Recording Badge */}
          {showRecordingIndicator && !isLoading && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-danger/90 px-2 py-1 rounded-full">
              <div className="w-2 h-2 rounded-full bg-danger-foreground animate-pulse" />
              <span className="text-foreground text-xs font-bold">REC</span>
            </div>
          )}
        </div>
      </div>

      {/* Camera Selection */}
      {!hideDeviceSelector && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-muted-foreground mb-2 text-left">Camera</label>
          <div className="relative">
            <select
              value={selectedId}
              onChange={(e) => handleDeviceChange(e.target.value)}
              disabled={disabled || devices.length === 0}
              className="w-full bg-background border border-border text-muted-foreground rounded-lg px-4 py-3 pl-10 appearance-none focus:ring-1 focus:ring-volt focus:border-volt focus:outline-none cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {devices.length === 0 ? (
                <option value="">No cameras found</option>
              ) : (
                devices.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${d.deviceId.slice(0, 5)}...`}
                  </option>
                ))
              )}
            </select>
            <VideoIcon className="absolute left-3 top-3.5 w-5 h-5 text-subtle-foreground pointer-events-none" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPreview;
